import { type App, type Component, warn } from "vue";
import { createApp } from "vue/dist/vue.esm-bundler.js";

import { BrandType } from "@appEnum/BrandType";
import { setupBugReportSystem } from "@utility/bugReportSetup";
import { determineZIndexForModal } from "@utility/domUtils";
import { isNullOrWhiteSpace } from "@utility/stringUtils";
import { raiseToastsAbove } from "@utility/toastUtils";
import "vue3-toastify/dist/index.css";
//Kept in s1.css: server-rendered and legacy pages use fa- classes without ever mounting icon.vue.
import "@css/shared/font-awesome-6/_font-awesome.export";
import "./interfaces/customWindow";
import { createPinia, type Pinia } from "pinia";
import { createRouter, createWebHistory } from "vue-router";
import Vue3Toasity, { type ToastContainerOptions } from "vue3-toastify/dist/index.mjs";
import { importS1WebLegacyCssBundles } from "../legacy/LegacyBundleImports";
import { SiteType } from "./appEnum/SiteType";
import type { IAppArgs } from "./interfaces/IAppArgs";
import { routes as cwRoutes } from "./routes/cw-routes";
import { routes as s1Routes } from "./routes/s1-routes";
import registerDirectives from "./startup/directives/registration";
import { initErrorCapture } from "./startup/errorCapture/errorCapture";
import { useAppStore } from "./stores/appStore";
import { useMenuStore } from "./stores/menuStore";
import { usePageStore } from "./stores/pageStore";
import { buildMismatchDetected, hasHideHubspotChatParam, isTestingSite } from "./utility/webServices";

interface appPluginArgs {
  siteTypeRouting?: SiteType;
}

//the master markup hands the start args to non-routed apps through these globals
interface appStartArgsWindow {
  hana?: { appStartArgs?: IAppArgs };
  cw?: { appStartArgs?: IAppArgs };
}

if (process.env.NODE_ENV !== "production") {
  window["VUE_DEVTOOLS_CONFIG"] = {
    openInEditorHost: `https://${window.location.hostname}:8999/`,
  };
}

const addCssBundles = (siteType?: SiteType): Promise<unknown> => {
  if (siteType === SiteType.S1) {
    return importS1WebLegacyCssBundles();
  }
  return Promise.resolve();
};

const registerAppPlugins = (app: App, args: appPluginArgs, appArgs?: IAppArgs) => {
  const pinia = createPinia();
  app.use(pinia);
  if (args.siteTypeRouting) {
    const router = createRouter({
      history: createWebHistory(),
      routes: args.siteTypeRouting === SiteType.CustomerWeb ? cwRoutes : s1Routes,
    });
    const appArgsStore = useAppStore(pinia);
    if (args.siteTypeRouting === SiteType.S1 || args.siteTypeRouting === SiteType.CustomerWeb) {
      initErrorCapture(app, args.siteTypeRouting);
    }
    if (args.siteTypeRouting === SiteType.S1) {
      setupBugReportSystem();
    }

    const pageStore = usePageStore(pinia);

    app.use(Vue3Toasity, {
      autoClose: 3000,
      position: "bottom-right",
      onOpen: () => raiseToastsAbove(determineZIndexForModal().modalZ),
    } as ToastContainerOptions);
    router.beforeEach(async (to, from) => {
      const previousGoogleTagType = appArgsStore.appArgs?.PageMeta?.GoogleTagType;
      const pm = await pageStore.getPageMeta(to.path);
      if (pm !== null) {
        appArgsStore.setCurrentPageMeta(pm);
      }
      const nextGoogleTagType = pm?.GoogleTagType;
      if (buildMismatchDetected.value || (previousGoogleTagType ?? null) !== (nextGoogleTagType ?? null)) {
        window.location.href = to.fullPath;
      }
    });
    app.use(router);
  }

  return { app, pinia };
};

/**
 *
 * @param rootComponent
 * @param rootComponentArgs
 * @returns Vue app instance
 */
const createNonRoutedApp = (rootComponent: Component, rootComponentArgs?: Record<string, unknown>): App => {
  if (!rootComponent) {
    console.warn(
      "==createNonRoutedApp()==> unknown root component argument",
      rootComponent,
      rootComponentArgs
    );
  }

  const startWindow = window as appStartArgsWindow;
  const args = startWindow.hana?.appStartArgs || startWindow.cw?.appStartArgs;
  includeBrandCssVars(args?.SiteTypeId, args?.BrandCssVars);
  addCssBundles(args?.SiteTypeId);
  if (!Object.prototype.hasOwnProperty.call(rootComponent, "name")) {
    (rootComponent as { name?: string }).name = "둘 - non routed";
  }
  const app = createApp(rootComponent, rootComponentArgs);
  const { pinia } = registerAppPlugins(app, {}, args);
  //hana/cw.appStartArgs arrive late from the master markup, so an empty read must not clobber a seeded store
  if (args) {
    useAppStore(pinia).appArgs = args;
  }
  initOpenReplay(args);

  registerDirectives(app);

  if (shouldHideHubspotChat(pinia)) {
    hideHubspotChat();
  }

  addGlobalTourOverlay(args);
  checkForTourBuildAgent(args);

  return app;
};

/**
 * Add global bug report modal to the page
 * Can be triggered via Ctrl+Shift+B or window.openBugReport()
 */
const addGlobalBugReportModal = async () => {
  // Create mount point for bug report modal
  const bugReportContainer = document.createElement("div");
  bugReportContainer.id = "bug-report-container";
  document.body.appendChild(bugReportContainer);

  // Dynamically import and mount the global bug report modal
  const { default: globalBugReportModal } =
    await import("@compUtility/bugReportTool/globalBugReportModal.vue");

  const bugReportApp = createApp(globalBugReportModal);
  bugReportApp.mount("#bug-report-container");

  console.info("[Bug Report] Global modal ready. Press Ctrl+Shift+B to open.");
};

let tourOverlayMounted = false;

/**
 * Mounts the global tour spotlight overlay, plus the admin Tour Builder toggle on the internal site.
 * The overlay runs for anonymous visitors on a brand domain, because customer-facing tours exist for
 * them and its two lookups are widened to an anonymous CustomerWeb caller. Those same lookups stay
 * authenticated on S1, so a logged-out visitor there is skipped entirely: the 401 would raise the
 * global "Access denied" dialog, which is what the login page shows.
 * The builder stays S1-and-logged-in: it resolves an admin right on mount, and customer-facing pages
 * are authored through its preview frame rather than by mounting it on the public site.
 * Mounted for both routed and non-routed pages, since a resumed multi-page tour can land on
 * either, and guarded so a page calling both createNonRoutedApp and startApp doesn't double-mount.
 */
const addGlobalTourOverlay = async (appArgs?: IAppArgs) => {
  const siteTypeId = appArgs?.SiteTypeId ?? SiteType.Unknown;
  const isLoggedIn = (appArgs?.CurrentUserObj?.UserId ?? 0) > 0;
  if (tourOverlayMounted || siteTypeId === SiteType.Unknown || (siteTypeId === SiteType.S1 && !isLoggedIn)) {
    return;
  }
  tourOverlayMounted = true;

  const tourOverlayContainer = document.createElement("div");
  tourOverlayContainer.id = "tour-overlay-container";
  document.body.appendChild(tourOverlayContainer);

  const { default: TourOverlay } = await import("@compUtility/tourBuilder/tourOverlay.vue");
  createApp(TourOverlay).mount("#tour-overlay-container");

  if (siteTypeId !== SiteType.S1) {
    return;
  }

  const tourBuilderContainer = document.createElement("div");
  tourBuilderContainer.id = "tour-builder-container";
  document.body.appendChild(tourBuilderContainer);

  const { default: GlobalTourBuilderToggle } =
    await import("@compUtility/tourBuilder/globalTourBuilderToggle.vue");
  createApp(GlobalTourBuilderToggle).mount("#tour-builder-container");
};

/**
 * Unlocks the Tour Builder's element picker on a customer-facing page. The server sets the flag only
 * for a validated token minted for a holder of the tour management right, so nothing here loads for
 * an ordinary visitor — and the frame's cookie, which a browser may withhold as third-party, is not
 * relied on to decide it.
 */
const checkForTourBuildAgent = async (appArgs?: IAppArgs) => {
  if (window.parent === window || !appArgs?.IsTourBuildMode) {
    return;
  }

  const { initTourBuildAgent } = await import("@compUtility/tourBuilder/tourBuildAgent");
  initTourBuildAgent(appArgs);
};

let consoleBannerShown = false;

const showConsoleBanner = (hostName?: string): void => {
  if (consoleBannerShown) {
    return;
  }
  consoleBannerShown = true;
  const scheduleIdle =
    window.requestIdleCallback ?? ((callback: () => void) => window.setTimeout(callback, 1));
  scheduleIdle(() => {
    void import("./utility/consoleBanner/s1ConsoleBanner").then((banner) =>
      banner.showS1ConsoleBanner(hostName)
    );
  });
};

const startApp = (appArgs: IAppArgs) => {
  includeBrandCssVars(appArgs?.SiteTypeId, appArgs?.BrandCssVars);
  const cssReady = addCssBundles(appArgs?.SiteTypeId);
  const { app, pinia } = registerAppPlugins(
    createApp({ name: "둘 - routed" }),
    {
      siteTypeRouting: appArgs.SiteTypeId,
    },
    appArgs
  );

  useAppStore(pinia).appArgs = appArgs;
  //start the menus now, beside the page meta and page chunk, rather than when the master template mounts.
  //Logged-out S1 visitors are skipped: the 401 would raise the global "Access denied" dialog
  if (appArgs?.SiteTypeId === SiteType.S1 && (appArgs?.CurrentUserObj?.UserId ?? 0) > 0) {
    void useMenuStore(pinia).getMenus();
  }

  registerDirectives(app);

  //the initial navigation already started at app.use(router), so this only waits when the css is the slowest part
  void cssReady
    .catch((e) => warn("==Could not import legacy css bundles==>", e))
    .finally(() => app.mount("#s1"));
  initOpenReplay(appArgs);

  if (shouldHideHubspotChat(pinia)) {
    hideHubspotChat();
  }

  // Add global bug report modal (S1 only)
  if (appArgs.SiteTypeId === SiteType.S1) {
    addGlobalBugReportModal();
  }

  addGlobalTourOverlay(appArgs);
  checkForTourBuildAgent(appArgs);
  showConsoleBanner(appArgs?.SourceHost);
};

let importedBrandCssFile: string = "";

const includeBrandCssVars = (siteType?: SiteType, brandCssVars?: string): void => {
  if (
    siteType === undefined ||
    siteType === SiteType.S1 ||
    (siteType === SiteType.CustomerWeb && (brandCssVars ?? "") !== "")
  ) {
    const cssRootId = "s1-style-vars";
    let $el = document.getElementById(cssRootId);
    const cssFile = siteType === undefined || siteType === SiteType.S1 ? "ShipleyOne" : brandCssVars;

    if (
      !isNullOrWhiteSpace($el?.innerHTML) ||
      !isNullOrWhiteSpace(importedBrandCssFile) ||
      isNullOrWhiteSpace(cssFile)
    ) {
      return;
    }
    importedBrandCssFile = cssFile!;
    import(`@css/brands/${cssFile}.vue`)
      .then((x) => {
        const styleApp = createApp(x.default);
        $el = document.getElementById(cssRootId);
        if (!$el) {
          $el = document.createElement("div");
          $el.id = cssRootId;
          document.body.appendChild($el);
        }

        if ($el.innerHTML === "") {
          styleApp.mount(`#${cssRootId}`);
        }
      })
      .catch((e) => {
        warn("==Could not import brand specific css var file==>", e);
      });
  }
};

let openReplayStarted = false;

// Initialize OpenReplay tracker
// inlineCss: 3 =>
// property stylesheets will then be captured as part of each replay (instead of being centrally cached by the server)
const initOpenReplay = async (appArgs?: IAppArgs): Promise<void> => {
  if (!appArgs?.OpenReplayProjectKey || openReplayStarted) {
    return;
  }
  openReplayStarted = true;
  try {
    //this is asynced here because this lib is sorta big to include in the main s1 entrypoint
    const { tracker } = await import("@openreplay/tracker");
    tracker.configure({
      projectKey: appArgs.OpenReplayProjectKey,
      ingestPoint: appArgs.OpenReplayIngestPoint,
      inlineCss: 3,
    });
    tracker.start();
    const user = appArgs?.CurrentUserObj;
    if (user !== null && !isNullOrWhiteSpace(user?.Email)) {
      tracker.identify(user!.Email ?? "");
    }
    tracker.setMetadata("siteType", SiteType[appArgs.SiteTypeId]);
    tracker.setMetadata("brandType", BrandType[appArgs.SiteBrandTypeId ?? 0]);
  } catch (e) {
    //reset so a later call can retry (e.g. transient chunk-load failure on a flaky network)
    openReplayStarted = false;
    warn("==Could not initialize OpenReplay==>", e);
  }
};

const shouldHideHubspotChat = (pinia: Pinia): boolean =>
  (hasHideHubspotChatParam() && isTestingSite()) || useAppStore(pinia).isCustomerMobileApp();

const hideHubspotChat = () => {
  setTimeout(() => {
    const iframe = document.getElementById("hubspot-conversations-iframe");
    if (iframe) {
      iframe.remove();
    }
  }, 500);
};

const resetOpenReplayStarted = () => {
  openReplayStarted = false;
};

export { createNonRoutedApp, resetOpenReplayStarted, showConsoleBanner, startApp };
